import React from 'react';
import { useIntl } from 'react-intl';

import { CheckoutPortalFormattedMessage } from 'components/Core/CheckoutPrtalForamattedMessage/CheckoutPortalFormattedMessage';
import FormattedMessageHTML from 'components/Core/FormattedMessageHTML/FormattedMessageHTML';
import { getLegalLinks } from 'shared/clientUtils';
import { colors, fontSize } from 'styles/cp';
import { DateFormat_Long } from 'types/constants';

interface payment {
  amount: number;
  paymentMethodType: string;
  isAutoPayOn?: boolean;
}

export interface PaymentDisclaimerProps {
  payment: payment;
  currency: string;
  dueDate: string;
  isBatchPayment?: boolean;
  selectedInvoicesCount?: number;
  isCardOnFileEnabled?: boolean;
  isInEnhancementsHoldoutExperiment?: boolean;
}

const PaymentDisclaimer: React.FC<PaymentDisclaimerProps> = ({
  currency,
  payment: { amount, paymentMethodType, isAutoPayOn = false },
  dueDate = '',
  isBatchPayment = false,
  selectedInvoicesCount = 0,
  isCardOnFileEnabled = false,
  isInEnhancementsHoldoutExperiment = false,
}) => {
  const intl = useIntl();
  // Calculate the actual charge date (3 days before due date for autopay)
  const getChargeDate = () => {
    if (!dueDate) return new Date();

    const dateStr = dueDate.replace(/-/g, '/');
    const dueDateObj = new Date(dateStr);

    if (isNaN(dueDateObj.getTime())) {
      return new Date();
    }

    // Check if due date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateComparison = new Date(dueDateObj);
    dueDateComparison.setHours(0, 0, 0, 0);

    if (dueDateComparison < today) {
      return new Date();
    }

    // For autopay, subtract 3 days
    if (isAutoPayOn) {
      const chargeDate = new Date(dueDateObj);
      const currentDate = chargeDate.getDate();
      chargeDate.setDate(currentDate - 3);
      return chargeDate;
    }

    return dueDateObj;
  };

  const chargeDate = getChargeDate();

  const displayAmount = intl.formatNumber(amount, { style: 'currency', currency });
  let paymentMethodTypeDisclaimer;

  const { privacyUrl, tosUrl, caToSUrl } = getLegalLinks(intl.locale);
  const paymentMethod =
    paymentMethodType === 'bank' || paymentMethodType === 'pbb' || paymentMethodType === 'eft'
      ? intl
          .formatMessage({
            id: 'PAYFLOW_PAYMENT_TYPE_BANK_ACCOUNT',
            defaultMessage: 'bank account',
          })
          .toLowerCase()
      : intl
          .formatMessage({ id: 'PAYFLOW_PAYMENT_TYPE_CARD', defaultMessage: 'card' })
          .toLowerCase();
  const autoPayDisclaimerId =
    paymentMethodType === 'bank'
      ? 'PAYFLOW_PAYMENT_AUTO_PAY_BANK_DISCLAIMER'
      : 'PAYFLOW_PAYMENT_AUTO_PAY_DISCLAIMER';
  const autoPayDisclaimerDefaultMessage = `By selecting <b>Pay</b>, I accept the <a class='termsOfServiceFooter' href="{0}" target="_blank">Terms of Service</a> and have read and acknowledge the <a href="{1}" target="_blank">Privacy Statement</a>. I also allow Intuit to charge {2} to my {3} on {4} and periodically thereafter per my <a href="{5}" target="_blank">Billing Agreement</a>. payments will be charged 3 days before the due date.`;
  const autoPayDisclaimer = dueDate ? (
    <FormattedMessageHTML
      id={autoPayDisclaimerId}
      values={{
        0: tosUrl,
        1: privacyUrl,
        2: displayAmount,
        3: paymentMethod,
        4: isInEnhancementsHoldoutExperiment
          ? intl.formatDate(chargeDate, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
          : intl.formatDate(
              new Date(dueDate) < new Date() ? new Date() : new Date(dueDate.replace(/-/g, '/')),
              DateFormat_Long
            ),
        5: '/billing-agreement',
      }}
      defaultMessage={autoPayDisclaimerDefaultMessage}
    />
  ) : null;

  const cardOnFileTermsOfService = (
    <CheckoutPortalFormattedMessage
      id="CARD_ON_FILE_TERMS_OF_SERVICE"
      values={{
        2: displayAmount,
        3: paymentMethod,
        4: intl.formatDate(new Date().getTime(), DateFormat_Long),
        link1: (chunks: string) => (
          <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
            {chunks}
          </a>
        ),
        link2: (chunks: string) => (
          <a href={privacyUrl} target="_blank" rel="noreferrer">
            {chunks}
          </a>
        ),
      }}
      defaultMessage={
        'By clicking the button below, you allow Intuit to charge {2} to your {3} on {4} and also accept the <link1>Terms of Service</link1>. Our <link2>Global Privacy Statement</link2> will apply to any information you provide us.'
      }
    />
  );

  switch (paymentMethodType) {
    case 'bank':
      paymentMethodTypeDisclaimer = isAutoPayOn ? (
        autoPayDisclaimer
      ) : isBatchPayment && selectedInvoicesCount > 1 ? (
        <CheckoutPortalFormattedMessage
          id="PAYFLOW_PAYMENT_METHOD_BANK_BATCH_DISCLAIMER"
          values={{
            2: displayAmount,
            3: intl.locale !== 'fr' ? paymentMethod : '',
            4: intl.formatDate(new Date().getTime(), DateFormat_Long),
            link1: (chunks: string) => (
              <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            link2: (chunks: string) => (
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            b: (chunks: string) => <b>{chunks}</b>,
          }}
          defaultMessage={`By selecting <b>Pay</b>, you accept the <link1>Terms of Service</link1> and allow Intuit to charge {2} in separate transactions on {4}. Our <link2>Global Privacy Statement</link2> will apply to any information you provide us.`}
        />
      ) : isCardOnFileEnabled ? (
        cardOnFileTermsOfService
      ) : (
        <CheckoutPortalFormattedMessage
          id="PAYFLOW_PAYMENT_METHOD_BANK_DISCLAIMER"
          values={{
            1: privacyUrl,
            2: displayAmount,
            3: intl.locale !== 'fr' ? paymentMethod : '',
            4: intl.formatDate(new Date().getTime(), DateFormat_Long),
            5: '',
            link1: (chunks: string) => (
              <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            link2: (chunks: string) => (
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            linebreak: <br />,
            b: (chunks: string) => <b>{chunks}</b>,
          }}
          defaultMessage={`By selecting <b>Pay</b>, you accept the <link1>Terms of Service</link1>. Our <link2>Global Privacy Statement</link2> will apply to any personal information you provide us. You also allow Intuit to charge {2} to your {3} on {4}. {linebreak} You also provide written instructions for Intuit Inc. to obtain and periodically refresh information from bank verification data providers to validate your bank account information and for other fraud detection purposes.`}
        />
      );
      break;
    case 'eft':
      paymentMethodTypeDisclaimer = (
        <div>
          <CheckoutPortalFormattedMessage
            id="PAYFLOW_PAYMENT_METHOD_EFT_DISCLAIMER"
            values={{
              1: privacyUrl,
              2: displayAmount,
              3: intl.locale !== 'fr' ? paymentMethod : '',
              4: intl.formatDate(new Date().getTime(), DateFormat_Long),
              5: '',
              link1: (chunks: string) => (
                <a
                  className="termsOfServiceFooter"
                  href={caToSUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              ),
              link2: (chunks: string) => (
                <a href={privacyUrl} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
              linebreak: <br />,
              b: (chunks: string) => <b>{chunks}</b>,
            }}
            defaultMessage={`By selecting <b>Pay</b>, you accept the <link1>Terms of Service</link1> and have read and acknowledge the <link2>Privacy Statement</link2>. You also allow Intuit to charge {2} to your {3} on {4}.`}
          />
        </div>
      );
      break;
    case 'pbb':
      paymentMethodTypeDisclaimer = (
        <div>
          <CheckoutPortalFormattedMessage
            id="PAYFLOW_PAYMENT_METHOD_PBB_DISCLAIMER"
            values={{
              1: privacyUrl,
              2: displayAmount,
              3: intl.locale !== 'fr' ? paymentMethod : '',
              4: intl.formatDate(new Date().getTime(), DateFormat_Long),
              5: '',
              link1: (chunks: string) => (
                <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
              link2: (chunks: string) => (
                <a href={privacyUrl} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
              linebreak: <br />,
              b: (chunks: string) => <b>{chunks}</b>,
            }}
            defaultMessage={`By selecting <b>Continue</b>, you accept the <link1>Terms of Service</link1>. Our global <link2>Privacy Statement</link2> will apply to any personal information you provide us. You also allow Intuit to charge {2} to your {3} on {4}.`}
          />
        </div>
      );
      break;
    case 'dc,cc':
    case 'dc':
    case 'cc':
      paymentMethodTypeDisclaimer = isAutoPayOn ? (
        autoPayDisclaimer
      ) : isBatchPayment && selectedInvoicesCount > 1 ? (
        <CheckoutPortalFormattedMessage
          id="PAYFLOW_PAYMENT_METHOD_CC_DC_BATCH_DISCLAIMER"
          values={{
            2: displayAmount,
            3: intl.locale !== 'fr' ? paymentMethod : '',
            4: intl.formatDate(new Date().getTime(), DateFormat_Long),
            link1: (chunks: string) => (
              <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            link2: (chunks: string) => (
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            b: (chunks: string) => <b>{chunks}</b>,
          }}
          defaultMessage={`By selecting <b>Pay</b>, you accept the <link1>Terms of Service</link1> and allow Intuit to charge {2} in separate transactions on {4}. Our <link2>Global Privacy Statement</link2> will apply to any information you provide us.`}
        />
      ) : isCardOnFileEnabled ? (
        cardOnFileTermsOfService
      ) : (
        <CheckoutPortalFormattedMessage
          id="PAYFLOW_PAYMENT_METHOD_CC_DC_DISCLAIMER"
          values={{
            1: privacyUrl,
            2: displayAmount,
            3: intl.locale !== 'fr' ? paymentMethod : '',
            4: intl.formatDate(new Date().getTime(), DateFormat_Long),
            5: '',
            link1: (chunks: string) => (
              <a className="termsOfServiceFooter" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            link2: (chunks: string) => (
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            b: (chunks: string) => <b>{chunks}</b>,
          }}
          defaultMessage={`By selecting <b>Pay</b>, you accept the <link1>Terms of Service</link1>. Our <link2>Global Privacy Statement</link2> will apply to any personal information you provide us. You also allow Intuit to charge {2} to your {3} on {4}.`}
        />
      );
      break;
    case 'ap':
      paymentMethodTypeDisclaimer = (
        <CheckoutPortalFormattedMessage
          id="PAYMENT_METHOD_APPLE_PAY_DISCLAIMER"
          values={{
            2: displayAmount,
            3: intl.locale !== 'fr' ? paymentMethod : '',
            4: intl.formatDate(new Date().getTime(), DateFormat_Long),
            b: (chunks: string) => <b>{chunks}</b>,
            link1: (chunks: string) => (
              <a className="link" id="applePayTOS" href={tosUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            link2: (chunks: string) => (
              <a
                className="link"
                id="applePayPrivacyPolicy"
                href={privacyUrl}
                target="_blank"
                rel="noreferrer"
              >
                {chunks}
              </a>
            ),
          }}
          defaultMessage={`By selecting <b>Apple Pay</b>, you accept the <link1>Terms of Service</link1>. Our <link2>Global Privacy Statement</link2> will apply to any personal information you provide us. You also allow Intuit to charge {2} to your {3} on {4}.`}
        />
      );
      break;

    case 'pp':
      paymentMethodTypeDisclaimer = (
        <CheckoutPortalFormattedMessage
          id="PAYFLOW_PAYMENT_METHOD_BANK_DISCLAIMER_2"
          values={{
            a: (chunks: string) => (
              <a
                className="link"
                id="paypalAgreement\"
                href="https://www.paypal.com/us/webapps/mpp/ua/useragreement-full"
                target="_blank"
                rel="noreferrer"
              >
                {chunks}
              </a>
            ),
          }}
          defaultMessage={
            'Payments are processed by PayPal. Any payment you make with PayPal is subject to the <a>PayPal User Agreement</a> which will prevail over any contradictory terms.'
          }
        />
      );
      break;
  }

  return (
    <div className="message">
      <div className="cp-link payment-disclaimer">{paymentMethodTypeDisclaimer}</div>

      <style jsx>
        {`
          .message {
            font-family: AvenirNextforINTUIT-Regular;
            font-size: ${fontSize.xxs};
            color: ${colors.ghostGray};
            font-style: normal;
            font-stretch: normal;
            line-height: 1.33;
            letter-spacing: -0.1px;
            user-select: none;

            .payment-disclaimer {
              margin: 0;
              margin-bottom: 10px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentDisclaimer;
